/**
 * /tenants/$slug/authentication — layout for the Authentication hub.
 *
 * Renders the shared AuthenticationTabs strip once, above every hub page's content via
 * <Outlet/> (sign-in, authenticators, identity-trust, conditional-access, locations,
 * fallback). One mount point instead of repeating the tab strip on each page — the same
 * "section layout owns the tabs" pattern, the cleaner alternative to per-page rendering.
 * See docs/product-areas.md.
 */

import { createFileRoute, getRouteApi, Outlet } from "@tanstack/react-router";
import { Breadcrumbs, PageHeader } from "@pureid/ui";
import { AuthenticationTabs } from "@/components/authentication-tabs";
import { DirtyStateProvider } from "@/features/tenant-settings/components/dirty-state";

export const Route = createFileRoute("/_authed/tenants/$slug/authentication")({
  component: AuthenticationHubLayout,
});

const tenantRouteApi = getRouteApi("/_authed/tenants/$slug");

function AuthenticationHubLayout() {
  const { slug } = Route.useParams();
  const { tenant } = tenantRouteApi.useRouteContext();
  // The layout owns the hub's single page shell — ONE <h1> + breadcrumbs + the tab strip —
  // so every tab renders the same chrome and the folded sections below stay <h2>s (one
  // heading level per page; matches the access-governance hub). Each tab's own primary
  // actions live in their section header rows, not here.
  //
  // It also owns two responsibilities for the useSettingsForm tabs (sign-in, identity-trust):
  // (1) DirtyStateProvider — the beforeunload (close/refresh/external-nav) unsaved-changes
  // guard lives in it; without it the guard silently no-ops. (2) the dynamic bottom padding
  // that reads the StickySaveBar's published height, so the bar never overlays the last field.
  return (
    <DirtyStateProvider>
      <div
        className="space-y-6"
        style={{
          paddingBottom: "calc(var(--settings-save-bar-height, 0px) + 1rem)",
        }}
      >
        <Breadcrumbs
          items={[
            { label: "Tenants", href: "/tenants" },
            { label: tenant.name, href: `/tenants/${slug}` },
            { label: "Authentication" },
          ]}
        />
        <PageHeader
          title="Authentication"
          description="How people sign in to this tenant and the policies that govern it."
        />
        <AuthenticationTabs tenantSlug={slug} />
        <Outlet />
      </div>
    </DirtyStateProvider>
  );
}
